import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import { Card, CardContent, Grid, Box, Stack, Link, Button, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { candidateAggregatedDataWithJobId, CandidateDataDeleteRequestDTO } from 'wisestep-api';
import {
  timeSince,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';

import { dataDeleteCandidate } from './DataDeleteCandidateQuery';
export interface DeleteReqCandidateCardProps {
  candidateIndex?: number;
  candidateDeleteDTO: CandidateDataDeleteRequestDTO;
  candidateList: CandidateDataDeleteRequestDTO[];
}

const StyledButton = styled(Button)({
  border: WsColors.Red500,
  color: WsColors.White,
  textTransform: 'none',
  fontWeight: 'bold',
  backgroundColor: WsColors.Red400,
  ':hover': {
    backgroundColor: WsColors.Red500,
    border: WsColors.Red500,
  },
});

const DeleteReqCandidateCard = ({
  candidateIndex,
  candidateDeleteDTO,
  candidateList,
}: DeleteReqCandidateCardProps) => {
  const navigateUrl = `/candidates/mappings?email=${candidateDeleteDTO.email ?? ''}`;
  const [candidateDeleteStatusField, setCandidateDeleteStatusField] = useState<string>('Pending');
  const deleteStatus = 'Delete status: ';
  const openModal = useConversationWidgetStore((state) => state.updateId);
  const [cvIndex, setCvIndex] = useState<number | null>(null);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);
  const navigate = useConversationWidgetStore((state) => state.navigate);
  const navigationState = useConversationWidgetStore((state) => state.navigationState);
  const updateNavigationState = useConversationWidgetStore((state) => state.updateNavigationState);
  const setIsWidgetOpen = useConversationWidgetStore((state) => state.setIsWidgetOpen);
  const setEraseData = useConversationWidgetStore((state) => state.setEraseData);

  useEffect(() => {
    if (navigationState && navigate && cvIndex !== null) {
      if (cvIndex > 0 && cvIndex <= candidateList.length - 1 && navigate == 'prev') {
        const index = cvIndex - 1;
        setCvIndex(index);
        setEraseData(true);
        const indexId = candidateList[index].id;
        indexId && indexId !== undefined && openModal(indexId);
      } else if (cvIndex >= 0 && cvIndex < candidateList.length - 1 && navigate == 'next') {
        const index = cvIndex + 1;
        setCvIndex(index);
        setEraseData(true);
        const indexId = candidateList[index].id;
        indexId && indexId !== undefined && openModal(indexId);
      } else {
        setIsWidgetOpen(false);
      }
      updateNavigationState(false);
    }
  }, [
    candidateList,
    cvIndex,
    navigate,
    navigationState,
    openModal,
    setEraseData,
    setIsWidgetOpen,
    updateNavigationState,
  ]);

  const onCVClick = async (candidateEmail: string) => {
    updateFromPage('');
    candidateIndex && setCvIndex(candidateIndex);
    const response = await candidateAggregatedDataWithJobId(
      {
        email: candidateEmail,
        candidateProfileURLs: [],
        linkedInURL: '',
      },
      null
    );

    setEraseData(true);
    openModal(response.recentInteractions[0].id);
  };

  useEffect(() => {
    setCandidateDeleteStatusField(candidateDeleteDTO.isProcessed ? 'Deleted' : 'Pending');
  }, [candidateDeleteDTO.isProcessed]);

  const onDataDeleteClick = async (candidateEmail: string) => {
    const response = await dataDeleteCandidate(candidateEmail);
    if (response) {
      setCandidateDeleteStatusField('Deleted');
    }
  };

  return (
    <Grid mt={2} container columnSpacing={3}>
      <Grid item xs={12}>
        <Card sx={{ minHeight: '3vw', width: '100%' }}>
          <CardContent>
            <Box>
              <Grid container alignItems="center">
                <Grid item xs>
                  <Grid container spacing={2} p={2} pb={1}>
                    <PersonIcon color="primary" />

                    <WsTypography bold variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK}>
                      {candidateDeleteDTO.email}
                    </WsTypography>

                    <Link component={RouterLink} to={navigateUrl} target="_blank" underline="none">
                      <Grid item pl={1}>
                        <WsTypography
                          variant={WsTypoVariants.TITLE}
                          cursor="pointer"
                          color={WsTypoColors.PRIMARY}
                        >
                          View in Search Candidates
                        </WsTypography>
                      </Grid>
                    </Link>
                  </Grid>
                </Grid>

                <Grid item pl={22}>
                  <WsTypography
                    display="inline"
                    variant={WsTypoVariants.SUB_TITLE}
                    color={WsTypoColors.SECONDARY}
                  >
                    {deleteStatus}
                  </WsTypography>
                  <WsTypography
                    display="inline"
                    variant={WsTypoVariants.SUB_TITLE}
                    color={
                      candidateDeleteStatusField == 'Deleted'
                        ? WsTypoColors.RED
                        : WsTypoColors.SECONDARY
                    }
                  >
                    {candidateDeleteStatusField}
                  </WsTypography>
                </Grid>
              </Grid>
            </Box>
          </CardContent>

          <Box sx={{ backgroundColor: WsColors.TheBlues25 }} p={1.5}>
            <Grid container xs={12} direction="row" alignItems="center">
              {candidateDeleteStatusField != 'Deleted' ? (
                <>
                  <Grid item xs={7}>
                    <Stack direction="row" spacing={1}>
                      <AccessTimeIcon />
                      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                        Requested:{' '}
                        {`${
                          timeSince(String(candidateDeleteDTO.createdOn) ?? '') +
                            ' by ' +
                            String(candidateDeleteDTO.createdBy) ?? ''
                        }`}
                      </WsTypography>
                    </Stack>
                  </Grid>
                  <Grid item xs={3}>
                    <StyledButton
                      variant="outlined"
                      size="large"
                      onClick={() => onDataDeleteClick(candidateDeleteDTO.email ?? '')}
                    >
                      Erase the Personal Data
                    </StyledButton>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={10}>
                    <Stack direction="row" spacing={1}>
                      <AccessTimeIcon />
                      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                        Requested:{' '}
                        {`${
                          timeSince(String(candidateDeleteDTO.createdOn) ?? '') +
                            ' by ' +
                            String(candidateDeleteDTO.createdBy) ?? ''
                        }`}
                      </WsTypography>
                    </Stack>
                  </Grid>
                </>
              )}
              <Grid item xs={2}>
                <WsButton
                  variant="outlined"
                  size="large"
                  onClick={() => onCVClick && onCVClick(candidateDeleteDTO.email ?? '')}
                >
                  Candidate History
                </WsButton>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};
export default DeleteReqCandidateCard;
